'use client';

import { useMemo, useState } from 'react';
import { confidenceInterval, formatNumber, parseFiniteNumber } from '@/lib/browser-mathcalc-tools';

const PRESETS = [
  { label: '90%', value: '90' },
  { label: '95%', value: '95' },
  { label: '99%', value: '99' },
  { label: 'Custom', value: 'custom' },
];

export default function BrowserConfidenceIntervalCalculator() {
  const [mean, setMean] = useState('100');
  const [stdDev, setStdDev] = useState('15');
  const [sampleSize, setSampleSize] = useState('50');
  const [level, setLevel] = useState('95');
  const [customLevel, setCustomLevel] = useState('95');

  const confidencePercent = level === 'custom' ? parseFiniteNumber(customLevel) : Number(level);

  const result = useMemo(() => {
    if (confidencePercent === null) return { error: 'Enter a custom confidence level.' };
    return confidenceInterval(
      parseFiniteNumber(mean),
      parseFiniteNumber(stdDev),
      parseFiniteNumber(sampleSize),
      confidencePercent,
    );
  }, [mean, stdDev, sampleSize, confidencePercent]);

  const error = result && 'error' in result ? result.error : null;
  const data = result && !('error' in result) ? result : null;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Statistics</span>
        <h2 className="tool-live-title">Confidence Interval Calculator</h2>
        <p className="tool-live-subtitle">
          Estimate a confidence interval for a population mean from your sample statistics using the
          z-approximation. Runs live in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Sample mean (x̄)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setMean(event.target.value)}
                placeholder="100"
                type="text"
                value={mean}
              />
            </label>
            <label className="tool-live-field">
              <span>Sample standard deviation (s)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setStdDev(event.target.value)}
                placeholder="15"
                type="text"
                value={stdDev}
              />
            </label>
            <label className="tool-live-field">
              <span>Sample size (n)</span>
              <input
                inputMode="numeric"
                onChange={(event) => setSampleSize(event.target.value)}
                placeholder="50"
                type="text"
                value={sampleSize}
              />
            </label>
            <label className="tool-live-field">
              <span>Confidence level</span>
              <select onChange={(event) => setLevel(event.target.value)} value={level}>
                {PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            {level === 'custom' ? (
              <label className="tool-live-field">
                <span>Custom level (%)</span>
                <input
                  inputMode="decimal"
                  onChange={(event) => setCustomLevel(event.target.value)}
                  placeholder="95"
                  type="text"
                  value={customLevel}
                />
              </label>
            ) : null}
          </div>
          <p className="tool-live-panel-note">
            Formula: CI = x̄ ± z × (s / √n). This uses the z (normal) approximation, so it assumes a
            large sample or an approximately normal population.
          </p>
        </section>

        <section className="tool-live-preview">
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : data ? (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>z critical value</span>
                  <input readOnly type="text" value={formatNumber(data.z, 4)} />
                </div>
                <div className="tool-live-field">
                  <span>Standard error (s / √n)</span>
                  <input readOnly type="text" value={formatNumber(data.standardError, 6)} />
                </div>
                <div className="tool-live-field">
                  <span>Margin of error</span>
                  <input readOnly type="text" value={formatNumber(data.marginOfError, 6)} />
                </div>
                <div className="tool-live-field tool-live-field-full">
                  <span>Confidence interval</span>
                  <input
                    readOnly
                    type="text"
                    value={`${formatNumber(data.lower, 6)} to ${formatNumber(data.upper, 6)}`}
                  />
                </div>
              </div>
              <p className="tool-live-panel-note">Calculated locally in your browser.</p>
            </>
          ) : (
            <p className="tool-live-panel-note">
              Enter your sample statistics to see the interval.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
